import { inject, Injectable } from '@angular/core';
import {
  NativeDateAdapter,
  MAT_DATE_FORMATS,
  MatDateFormats,
  DateAdapter,
} from '@angular/material/core';

import { ProfileService } from '@tfs/auth';
import { formatDateByPattern, parseDateByPattern } from '@tfs/date-format';

@Injectable()
export class TfsDateAdapter extends NativeDateAdapter {
  private readonly profileService = inject(ProfileService);

  override format(date: Date, displayFormat: object): string {
    // The date input is configured with a string display format (see TFS_DATE_FORMATS);
    // for it we render the date in the user's preferred date format. Object display formats
    // (month/year and a11y labels) fall through to the native adapter.
    if (typeof displayFormat === 'string') {
      return formatDateByPattern(date, this.profileService.dateFormat()) ?? '';
    }
    return super.format(date, displayFormat);
  }

  override parse(value: unknown, parseFormat: unknown): Date | null {
    // Typed input arrives as a string and must be read per the user's chosen pattern, not the
    // native adapter's locale-heuristic Date.parse(). Programmatic values (Date/number/null, e.g.
    // from writeValue) keep the native behavior.
    if (typeof value !== 'string') {
      return super.parse(value, parseFormat);
    }
    return parseDateByPattern(value, this.profileService.dateFormat());
  }
}

export const TFS_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: null },
  display: {
    dateInput: 'dd.MM.yyyy',
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

export function provideTfsDateAdapter() {
  return [
    { provide: DateAdapter, useClass: TfsDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: TFS_DATE_FORMATS },
  ];
}
